import { atom as atomJ, useAtomValue } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useCallback } from 'react';
import { ICompanyDataModel } from '../../data-models/company.data-model';
import {
  batchRemoveCompaniesAtom,
  batchSetCompaniesAtom,
  getCompanySyncIfResolved,
} from '../state/CompanyStateJ';

export function useSaveCompanyData(overwrite = true) {
  const batchSaveCompanyData = useAtomCallback(
    useCallback(
      (_get, set, companies: ICompanyDataModel[]) => {
        const toSave = overwrite
          ? companies
          : // Skip companies already present in the sync mirror. After the
            // CompanyStateJ refactor `getCompanySyncIfResolved` is a pure Map
            // peek (no fetch), so this filter is cheap.
            companies.filter((company) => getCompanySyncIfResolved(company.id) == null);

        if (toSave.length > 0) {
          // Single-shot batch update: one Map copy of the sync mirror, regardless of N.
          set(batchSetCompaniesAtom, toSave);
        }
      },
      [overwrite]
    )
  );

  return { batchSaveCompanyData };
}

export function useSaveCompanyDataJ() {
  return useAtomCallback(
    useCallback((_get, set, companies: ICompanyDataModel[]) => {
      set(batchSetCompaniesAtom, companies);
    }, [])
  );
}

export function useRemoveCompanyDataJ() {
  return useAtomCallback(
    useCallback((_get, set, companyIds: number[]) => {
      set(batchRemoveCompaniesAtom, companyIds);
    }, [])
  );
}

export const companyMapAtom = atomJ<Map<number, ICompanyDataModel>>(new Map());

export function useSaveCompanyMap() {
  return useAtomCallback(
    useCallback((get, set, companies: ICompanyDataModel[]) => {
      set(companyMapAtom, (prev) => {
        // keep the identity when nothing is new; every consumer of the map re-derives on a new one
        if (companies.every((company) => prev.get(company.id) === company)) {
          return prev;
        }
        const newMap = new Map(prev);
        companies.forEach((company) => {
          newMap.set(company.id, company);
        });
        return newMap;
      });
    }, [])
  );
}

/**
 * Reactive lookup for companies present in {@link companyMapAtom}.
 *
 * Returns a stable function with the signature `(id) => ICompanyDataModel | null`
 * that performs a pure Map lookup (no fetches, no peeks). The returned
 * function's identity changes whenever the underlying map changes, which lets
 * downstream `useMemo` / `useCallback` consumers (e.g. AG Grid column defs)
 * rebuild reactively as more companies are populated.
 *
 * Intended for the PortfolioOverview page, where {@link companyMapAtom} is
 * fully populated by `useInitializeMetricsData`. Other surfaces should not
 * rely on this hook unless they also populate the map.
 */
export function useCompanyMapLookup() {
  const companyMap = useAtomValue(companyMapAtom);
  return useCallback((id: number): ICompanyDataModel | null => companyMap.get(id) ?? null, [companyMap]);
}
